import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Consts from './Const'
import './LastYearsWinners.css'
import { FlexDiv } from './flexdiv'
import { DateTimeHelper } from './DateTimeHelper'
import moment from 'moment'
import Util from './Util'
import { ITopPlayerForPeriod } from './TopPlayerForPeriod'
import { gsap } from 'gsap'

interface ILastYearWinnersProps {
  year: number
  winners: ITopPlayerForPeriod[]
  idx: number
}

const getColorByWinnerIdx = (idx: number) => {
  switch (idx) {
    case 0:
      return 'gold'
    case 1:
      return 'silver'
    case 2:
    default:
      return '#cd7f32'
  }
}

const LastYearWinners = ({ year, winners, idx }: ILastYearWinnersProps) => {
  return (
    <FlexDiv align="center" style={{ padding: '1vh', backgroundColor: '#666', maxWidth: '15vh', overflow: 'clip' }} direction="column" gap={8}>
      <FlexDiv direction="column" style={{ fontSize: '2vh', fontWeight: 'bold', color: 'white' }}>
        {year}
      </FlexDiv>
      {winners.map((winner, winnerIdx) => {
        return (
          <FlexDiv key={`lyw${winner.Id}`} align="center" style={{ width: '100%', overflow: 'clip' }}>
            <FlexDiv>
              <img
                key={`LastYearWinner-Player-avatar1-${year}-${winnerIdx}`}
                className="LastYearWinner-Player-avatar"
                src={`data:image/png;base64,${winner.Avatar ?? Consts.noAvatar}`}
                alt="Player avatar"
                style={{ borderColor: getColorByWinnerIdx(winnerIdx) }}
              />
            </FlexDiv>

            <FlexDiv
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginLeft: '0.5vh',
                fontWeight: 'bold',
                color: getColorByWinnerIdx(winnerIdx),
                textAlign: 'left',
                fontSize: '1.5vh',
              }}
            >
              {winner.Username}
            </FlexDiv>
          </FlexDiv>
        )
      })}
    </FlexDiv>
  )
}

interface ILastYearsWinnersProps {
  organizationId: string
  activityType: number
  baseUrl: string
  latestHistoryId: string
  callback: (yearDataLength: number) => void
}

const getRelevantYears = (nowYear: number) => new Array(10).fill(null).map(_ => --nowYear)

const LastYearsWinners = ({ organizationId, activityType, baseUrl, latestHistoryId, callback }: ILastYearsWinnersProps) => {
  const [data, setData] = useState<Map<number, ITopPlayerForPeriod[]>>(new Map<number, ITopPlayerForPeriod[]>())

  const now = moment() //[2025,0,20])
  const year = now.year()

  useEffect(() => {
    const years = getRelevantYears(year)

    for (const year of years) {
      if (data.has(year)) continue

      fetch(`${baseUrl}/api/topplayersforyear?organizationId=${organizationId}&activityType=${activityType}&period=${year}0000&amount=3`)
        .then(res => {
          return res.json()
        })
        .then(res => {
          setData(existing => Util.pureMapSet(existing, year, res as ITopPlayerForPeriod[]))
        })
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year])

  const yearData = Array.from(data.entries())
    .sort((a, b) => b[0] - a[0])
    .filter(x => x[1].length > 0)

  useEffect(() => {
    callback(yearData.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  //#region Animate all winners
  const allWinnersContainerRef = useRef<HTMLDivElement>(null)
  const allWinnersRef = useRef<HTMLDivElement>(null)
  const [allWinnersContainerWidth, setAllWinnersContainerWidth] = useState(0)
  const [allWinnersWidth, setAllWinnersWidth] = useState(0)

  useEffect(() => {
    if (!allWinnersRef.current || !allWinnersContainerWidth || !allWinnersWidth) return

    if (allWinnersContainerWidth < allWinnersWidth) {
      const delta = allWinnersWidth - allWinnersContainerWidth
      const durationPer100px = 1.5
      const duration = (delta / 100) * durationPer100px
      const pauseSeconds = 2 // <-- hvor længe den "fryser" i hver ende

      gsap.killTweensOf(allWinnersRef.current)
      gsap.set(allWinnersRef.current, { x: 0 }) // <-- her "sætter du position 0"

      const anim = gsap.to(allWinnersRef.current, {
        x: -delta, // Move to the right edge
        duration, // Duration of animation in seconds
        repeat: -1, // Infinite repeat
        yoyo: true, // Move back and forth
        ease: 'none', // 'power1.inOut', // 'power1.inOut', // Smoother easing
        repeatDelay: pauseSeconds, // <-- pause ved hver vending
        delay: pauseSeconds,
      })
      return () => {
        anim.kill() // Cleanup the animation
      }
    }
  }, [allWinnersContainerWidth, allWinnersWidth])

  useEffect(() => {
    if (!allWinnersContainerRef.current || !allWinnersRef.current) return

    const roC = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      setAllWinnersContainerWidth(width)
    })

    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      setAllWinnersWidth(width)
    })

    roC.observe(allWinnersContainerRef.current)
    ro.observe(allWinnersRef.current)
    return () => {
      roC.disconnect()
      ro.disconnect()
    }
  }, [])
  //#endregion Animate all winners

  return (
    <FlexDiv style={{ width: '100%', justifyContent: 'center', opacity: yearData.length ? 1 : 0 }}>
      <FlexDiv
        style={{
          width: '100%',
          backgroundColor: '#888',
          border: '3px solid #444',
          flexDirection: 'column',
          gap: 8,
          borderRadius: '3vh',
          overflow: 'hidden',
        }}
        align="center"
      >
        <FlexDiv className="LastYearWinners-title">🏆 Hall of Fame</FlexDiv>
        <FlexDiv
          ref={allWinnersContainerRef}
          style={{ borderTop: '3px solid #444', width: '100%', minWidth: 0, overflow: 'clip', contain: 'inline-size', height: '100%' }}
        >
          <div ref={allWinnersRef} style={{ display: 'flex', flexDirection: 'row' }}>
            {yearData.map((m, mIdx) => {
              return (
                <FlexDiv key={`lastYearWinners${mIdx}`}>
                  {mIdx !== 0 && <FlexDiv style={{ minWidth: 3, backgroundColor: '#444' }} />}
                  <LastYearWinners year={m[0]} winners={m[1]} idx={mIdx} />
                </FlexDiv>
              )
            })}
          </div>
          <FlexDiv style={{ minWidth: 3, backgroundColor: '#444' }} />
          <FlexDiv style={{ height: '100%', width: '100%', backgroundColor: '#666' }} />
        </FlexDiv>
      </FlexDiv>
    </FlexDiv>
  )
}

export default LastYearsWinners
